import React from 'react';
import { Modal as AntModal, ModalProps as AntModalProps, ModalFuncProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import { Button } from '../Button/Button';
import { useTranslation } from 'react-i18next';
import { getStore } from 'src/store/storeRef';
import { getTheme } from 'src/theming/connectTheme';

export type ModalStatus = 'default' | 'info' | 'success' | 'error' | 'warning' | 'confirm' | 'deleteConfirm';

export interface ModalProps extends AntModalProps {
  status?: ModalStatus;
}

const MODAL_WRAPPER_CLASS = 'ds-modal';
let styleElement: HTMLStyleElement | null = null;
let currentTheme: string | null = null;

/**
 * Styles for static modal methods (Modal.info, Modal.success, Modal.error, Modal.warning, Modal.confirm).
 * 
 * These methods render modals outside the React component tree, so JSS styles from useStyles()
 * are not applied. We inject these styles directly into the document head and update them
 * when the theme changes to ensure proper theming support.
 */
const generateStyles = (theme: any) => `
  /* Modal content/body styles */
  .${MODAL_WRAPPER_CLASS} .ant-modal-content {
    background-color: ${theme.color.semantic.background.neutral.secondary} !important;
    border-radius: ${theme.radius.md} !important;
    box-shadow: ${theme.shadow.lg} !important;
    overflow: hidden;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-header {
    background-color: ${theme.color.semantic.background.neutral.secondary} !important;
    border-bottom-color: ${theme.color.semantic.border.primary} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-title {
    color: ${theme.color.semantic.text.primary} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-body {
    background-color: ${theme.color.semantic.background.neutral.secondary} !important;
    color: ${theme.color.semantic.text.secondary} !important;
    font-size: ${theme.typography.fontSize.sm} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-footer {
    background-color: ${theme.color.semantic.background.neutral.secondary} !important;
    border-top-color: ${theme.color.semantic.border.primary} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-close-x {
    color: ${theme.color.semantic.text.secondary} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-title {
    color: ${theme.color.semantic.text.primary} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-content {
    color: ${theme.color.semantic.text.secondary} !important;
  }

  /* Icon colors */
  .${MODAL_WRAPPER_CLASS}-info .ant-modal-confirm-body > .anticon {
    color: ${theme.color.semantic.brand.default.primary} !important;
  }
  .${MODAL_WRAPPER_CLASS}-success .ant-modal-confirm-body > .anticon {
    color: ${theme.color.semantic.accent.green.strong} !important;
  }
  .${MODAL_WRAPPER_CLASS}-error .ant-modal-confirm-body > .anticon {
    color: ${theme.color.semantic.brand.danger.primary} !important;
  }
  .${MODAL_WRAPPER_CLASS}-warning .ant-modal-confirm-body > .anticon {
    color: ${theme.color.semantic.accent.orange.strong} !important;
  }

  /* Default button (base styles for all buttons, overridden by primary/danger) */
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn {
    background-color: ${theme.color.semantic.brand.default.subtle};
    border-color: ${theme.color.semantic.brand.default.secondary};
    border-width: ${theme.border.width.sm};
    border-style: solid !important;
    color: ${theme.color.semantic.brand.default.secondary};
    font-weight: ${theme.typography.fontWeight.medium};
    border-radius: ${theme.radius.sm};
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn:hover {
    background-color: ${theme.color.semantic.brand.default.muted};
    color: ${theme.color.semantic.brand.default.secondary};
    border-color: ${theme.color.semantic.brand.default.secondary};
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn:active,
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn:focus {
    background-color: ${theme.color.semantic.brand.default.light};
    color: ${theme.color.semantic.brand.default.primary};
    border-color: ${theme.color.semantic.brand.default.primary};
  }

  /* Primary button */
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-primary {
    background-color: ${theme.color.semantic.brand.default.primary} !important;
    border-color: ${theme.color.semantic.brand.default.primary} !important;
    font-weight: ${theme.typography.fontWeight.medium} !important;
    border-radius: ${theme.radius.sm} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-primary:hover {
    background-color: ${theme.color.semantic.brand.default.secondary} !important;
    border-color: ${theme.color.semantic.brand.default.secondary} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-primary:active,
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-primary:focus {
    background-color: ${theme.color.semantic.brand.default.primary} !important;
    border-color: ${theme.color.semantic.brand.default.primary} !important;
  }

  /* Danger button */
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-dangerous {
    background-color: ${theme.color.semantic.brand.danger.primary} !important;
    border-color: ${theme.color.semantic.brand.danger.primary} !important;
    color: ${theme.color.semantic.brand.danger.inverse} !important;
    font-weight: ${theme.typography.fontWeight.medium} !important;
    border-radius: ${theme.radius.sm} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-dangerous:hover {
    background-color: ${theme.color.semantic.brand.danger.muted} !important;
    border-color: ${theme.color.semantic.brand.danger.muted} !important;
  }
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-dangerous:active,
  .${MODAL_WRAPPER_CLASS} .ant-modal-confirm-btns .ant-btn-dangerous:focus {
    background-color: ${theme.color.semantic.brand.danger.primary} !important;
    border-color: ${theme.color.semantic.brand.danger.primary} !important;
  }
`;

const injectStyles = () => {
  // Guard for SSR environments
  if (typeof document === 'undefined') {
    return;
  }

  try {
    const store = getStore();
    const state = store?.getState();
    const newTheme = state?.uiTheme?.currentTheme || 'light';

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.setAttribute('data-ds-modal-styles', 'true');
      if (document.head) {
        document.head.appendChild(styleElement);
      }
      currentTheme = newTheme;
      styleElement.textContent = generateStyles(getTheme(state));
    } else if (currentTheme !== newTheme) {
      currentTheme = newTheme;
      styleElement.textContent = generateStyles(getTheme(state));
    }
  } catch (error) {
    console.error('Failed to inject modal styles:', error);
  }
};

const useStyles = createUseStyles((theme: ThemeType) => ({
  modal: {
    '& .ant-modal-content': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderRadius: theme.radius.md,
      boxShadow: theme.shadow.lg,
      overflow: 'hidden',
    },
    '& .ant-modal-header': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderBottomColor: theme.color.semantic.border.primary,
      padding: theme.spacing[4],
      borderTopLeftRadius: theme.radius.md,
      borderTopRightRadius: theme.radius.md,
      '& .ant-modal-title': {
        color: theme.color.semantic.text.primary,
        fontSize: theme.typography.fontSize.lg,
        fontWeight: theme.typography.fontWeight.semibold,
      },
    },
    '& .ant-modal-body': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: theme.color.semantic.text.secondary,
      fontSize: `${theme.typography.fontSize.sm} !important`,
      '& label, & p, & td, & th, & li': {
        fontSize: `${theme.typography.fontSize.sm} !important`,
      },
    },
    '& .ant-modal-close': {
      color: theme.color.semantic.text.secondary,
      '&:hover': {
        color: theme.color.semantic.text.primary,
      },
    },
    '& .ant-modal-footer': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderTopColor: theme.color.semantic.border.primary,
      borderBottomLeftRadius: theme.radius.md,
      borderBottomRightRadius: theme.radius.md,
      '& .ant-btn': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& .ant-btn-primary:disabled, & .ant-btn-primary.ant-btn-disabled, & .ant-btn-primary:disabled:hover, & .ant-btn-primary:disabled:active, & .ant-btn-primary:disabled:focus, & .ant-btn-primary.ant-btn-disabled:hover, & .ant-btn-primary.ant-btn-disabled:active, & .ant-btn-primary.ant-btn-disabled:focus': {
        backgroundColor: `${theme.color.semantic.background.neutral.soft} !important`,
        borderColor: `${theme.color.semantic.border.subtle} !important`,
        color: `${theme.color.semantic.text.soft} !important`,
        opacity: `${theme.opacity[70]} !important`,
      },
    },
  },
}));

const ModalInner: React.FC<ModalProps> = ({
  status = 'default',
  className = '',
  footer,
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const renderFooter = () => {
    if (footer !== undefined) {
      return footer;
    }

    const { okText, cancelText, okButtonProps, cancelButtonProps, okType, onOk, onCancel, confirmLoading } = rest;

    const showOk = okText || onOk;
    const showCancel = cancelText || onCancel;

    if (!showOk && !showCancel) {
      return undefined;
    }

    const buttons: React.ReactNode[] = [];

    if (showCancel) {
      buttons.push(
        <Button
          key="cancel"
          type="secondary"
          onClick={onCancel}
          {...cancelButtonProps}
        >
          {cancelText ?? t('Cancel')}
        </Button>
      );
    }

    if (showOk) {
      buttons.push(
        <Button
          key="ok"
          type="primary"
          danger={okType === 'danger'}
          onClick={onOk}
          loading={confirmLoading}
          {...okButtonProps}
        >
          {okText ?? 'OK'}
        </Button>
      );
    }

    return buttons;
  };

  return (
    <AntModal
      {...rest}
      footer={renderFooter()}
      className={`${classes.modal} ${className}`.trim()}
    />
  );
};

const wrapModalMethod = (
  method: typeof AntModal.info,
  typeClass: string
) => (props: ModalFuncProps) => {
  injectStyles();
  
  return method({
    ...props,
    className: `${MODAL_WRAPPER_CLASS} ${typeClass} ${props.className || ''}`.trim(),
  });
};

const Modal = Object.assign(ModalInner, {
  info: wrapModalMethod(AntModal.info, `${MODAL_WRAPPER_CLASS}-info`),
  success: wrapModalMethod(AntModal.success, `${MODAL_WRAPPER_CLASS}-success`),
  error: wrapModalMethod(AntModal.error, `${MODAL_WRAPPER_CLASS}-error`),
  warning: wrapModalMethod(AntModal.warning, `${MODAL_WRAPPER_CLASS}-warning`),
  confirm: wrapModalMethod(AntModal.confirm, `${MODAL_WRAPPER_CLASS}-confirm`),
  destroyAll: AntModal.destroyAll,
});

export default Modal;
